/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useContentZoneContext } from "cms/components/context";
import { setFieldComponentProperties } from "data/store/reducers/model";
import { produce } from "immer";
import { set } from "lodash";
import { useCallback, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { useDispatch } from "react-redux";
import { useDebounce } from "react-use";
import { useLatestFieldPosition } from "./use-latest-field-position";
import { Logger, green } from "cms/utils/log/logger";

/*
     TODO: (not urgent) this code is advanced (good; most likely needed), but is not easy to understand.
       need to produce a secondary readme to explain in better depth the use of:
       - updateFactory and its use of a curried function
       - update and its use of 'produce' from immer
       - how dot paths are being used
 */

const useUpdateComponent = () => {
    
    const dispatch = useDispatch();

    const { component: initialComponent } = useContentZoneContext();

    /**
     * Records the deltas (or diffs) made to the component as a map of their
     * path and value.
     * @type {import('react').MutableRefObject<Map<string, any>>}
     */
    const componentUpdates = useRef(new Map());
    
    const fieldPositionRef = useLatestFieldPosition();
    const [component, setComponent] = useState(initialComponent);
    
    useDebounce(() => {
        const hasChanged = !isEqual(component, initialComponent);
        if (hasChanged) {
            Logger.info(green(`[update-component] hasChanged '${hasChanged}'`), initialComponent, component);
            // extract path/value tuples and immediately reset the map
            const properties = Array.from(componentUpdates.current.entries());
            componentUpdates.current.clear();

            const guid = component.guid;
            // Logger.info(green(`[update-component] saving component '${guid}'`))
            Logger.info(green(`[update-component] saving component '${guid}'`));

            dispatch(setFieldComponentProperties({
                fieldPosition: fieldPositionRef.current,
                guid,
                properties
            }));
        }

    }, 500, [component]);
    
    const update = useCallback(
        /**
         * @param {string} path 
         * @param {any} value 
         */
        (path, value) => {
            componentUpdates.current.set(path, value);
            setComponent(
                produce((current) => {
                    set(current, path, value);
                })
            );
        },
        []
    );
    
    const updateFactory = useCallback(
        /**
         * @param {string} path
         */
        (path) => (value) => update(path, value),
        [update]
    );

    return {
        /**
         * Local state of the component.
         */
        component,

        /**
         * Dispatch for setting the local state in its entirety.  Be careful
         * when doing this as we can easily truncate data changes from outside
         * of this local context.
         * 
         * Use `update` instead.
         * 
         * @deprecated
         */
        setComponent,

        /**
         * Primary method of updating a component.  It accepts a path and a
         * value and is responsible for triggering the save pipepline once
         * called.
         * 
         * ```JS
         * const { update } = useUpdateComponent()
         * const onMyChange = (value) => {
         *     update('dot.path.to.target', value)
         * }
         * ```
         */
        update,

        /**
         * Convenience factory that creates a function which will accept a value to
         * be saved to the indicated path.
         * 
         * ```JS
         * const { updateFactory } = useUpdateComponent()
         * const onMyChange = updateFactory('dot.path.to.target')
         * ```
         */
        updateFactory
    };
};

export { useUpdateComponent };
